import { AsyncPipe, NgStyle } from '@angular/common';
import { Component, EventEmitter, OnInit, Output, inject } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { NgbDateStruct, NgbDatepickerModule } from '@ng-bootstrap/ng-bootstrap';
import { TransactionAuthorizationComponent, UserMessagesComponent } from '@zero/shared/ui-web-components';
import { ButtonComponent } from '@zero/shared/ui-web-design-system';
import { CustomerStateFacade } from '@zero/shared/util-global-state';
import {
  AuthPayloadTicket,
  AuthorizationData,
  FileToUpload,
  Ticket,
  TicketData,
  TransactionAuthorizationResult,
} from '@zero/shared/util-models';
import { GermanNgbDateValidatorDirective, TicketDialogService, UserMessagesHolderService } from '@zero/shared/util-web-app-services';
import { MAX_UPLOAD_FILE_SIZE } from 'constants/shared-constants';
import { filter, take } from 'rxjs/operators';

@Component({
  selector: 'self-service-update-identdoc',
  templateUrl: './update-identdoc.component.html',
  imports: [
    FormsModule,
    NgbDatepickerModule,
    GermanNgbDateValidatorDirective,
    NgStyle,
    TransactionAuthorizationComponent,
    UserMessagesComponent,
    AsyncPipe,
    ButtonComponent,
  ],
})
export class UpdateIdentdocComponent implements OnInit {
  public readonly messageService = inject(UserMessagesHolderService);

  public files: FileToUpload[] = [];
  public errors: string[] = [];
  public validToDatePickerMinDate: NgbDateStruct;
  public dataToAuthorize: AuthorizationData<AuthPayloadTicket>;
  public finished = false;

  @Output() public updateResult = new EventEmitter<string>();

  readonly #customerStateFacade = inject(CustomerStateFacade);
  readonly #ticketDialogService = inject(TicketDialogService);

  public ngOnInit(): void {
    this.messageService.clearAllMessages();
    this.initDateRangesForValidToDatePicker();
  }

  private initDateRangesForValidToDatePicker(): void {
    const today = new Date();
    today.setDate(today.getDate() + 1);

    this.validToDatePickerMinDate = {
      year: today.getFullYear(),
      month: today.getMonth() + 1,
      day: today.getDate(),
    } as NgbDateStruct;
  }

  public submitForm(f: NgForm): void {
    this.errors = [];
    this.messageService.clearAllMessages();

    if (f.valid && !this.filesInProgress()) {
      this.startAuthorization(f);
    }
  }

  public filesInProgress(): boolean {
    return this.files.filter((file) => file.uploadInProgress).length > 0;
  }

  public submitFileUpload(fileUpload: NgForm, fileUploadElem: HTMLInputElement): void {
    this.errors = [];
    this.messageService.clearAllMessages();
    const filesToUpload: FileToUpload[] = [];

    if (fileUploadElem.files.length > 2) {
      this.errors.push('Bitte wähle maximal 2 Dateien aus');
      fileUpload.reset('tempFiles');
      return;
    }

    // FileList does neither support for-of nor map
    // eslint-disable-next-line @typescript-eslint/prefer-for-of
    for (let index = 0; index < fileUploadElem.files.length; index++) {
      const file = fileUploadElem.files[index];
      const fileTooLarge = file.size > MAX_UPLOAD_FILE_SIZE;
      filesToUpload.push({
        fileData: file,
        uploadInProgress: false,
        uploadProgress: 0,
        fileName: file.name,
        fileRefForTicket: undefined,
        uploadError: fileTooLarge ? 'Maximale Dateigröße von 9MB überschritten.' : undefined,
      });
    }

    filesToUpload.forEach((fileToUpload) => {
      this.files.push(fileToUpload);
      if (!fileToUpload.uploadError) {
        this.#ticketDialogService.upload(fileToUpload).subscribe((result) => {
          if (!result.success) {
            if (result.message) {
              this.errors.push(result.message);
            } else {
              this.errors.push(`Datei ${fileToUpload.fileName} konnte nicht hochgeladen werden.`);
            }
          }
        });
      }
    });

    fileUpload.reset('tempFiles');
  }

  public deleteUploadedFile(fileToDelete: FileToUpload) {
    this.#ticketDialogService.delete(fileToDelete).subscribe(() => {
      this.errors = [];
      this.messageService.clearAllMessages();

      // whether successful or not, we always remove the file from the dialog
      this.files = this.files.filter((file) => file.fileRefForTicket !== fileToDelete.fileRefForTicket);
    });
  }

  public cancelUpdateIdentDoc() {
    this.files = [];
    this.dataToAuthorize = undefined;
    this.updateResult.emit('Aktualisierung abgebrochen');
  }

  private startAuthorization(f: NgForm) {
    this.#customerStateFacade.customerBaseData$
      .pipe(
        filter((res) => !!res),
        take(1),
      )
      .subscribe((cbd) => {
        const ticket = new Ticket();
        ticket.customerId = cbd.customerId;
        ticket.type = 'IDENTDOC_UPLOAD';
        ticket.ticketData = new TicketData();
        if (!ticket.ticketData.additionalProperties) {
          ticket.ticketData.additionalProperties = {};
        }

        ticket.ticketData.additionalProperties[TicketData.ATTR_IDENTDOCUPDATE_VALIDUNTIL] = this.convertDocValidUntil(
          f.value.identDocValidUntil,
        );
        ticket.ticketData.additionalProperties[TicketData.ATTR_IDENTDOCUPDATE_CONFIRMVALID] = f.value.confirmIdentDocValid;
        ticket.ticketData.additionalProperties[TicketData.ATTR_ATTACHMENT_FILEREFS] = this.files.map((file) => file.fileRefForTicket);

        this.dataToAuthorize = new AuthorizationData<AuthPayloadTicket>(new AuthPayloadTicket(ticket));
      });
  }

  private convertDocValidUntil(formValue: any) {
    if (!formValue) {
      return undefined;
    }
    let validUntil = (formValue as Date).toJSON();
    if (validUntil.indexOf('T') > -1) {
      validUntil = validUntil.substring(0, validUntil.indexOf('T'));
    }
    return validUntil;
  }

  public authorizedTransactionResult(result: TransactionAuthorizationResult): void {
    this.finished = result.success;
    if (result.success) {
      this.messageService.clearAllMessages();
      this.dataToAuthorize = undefined;
      this.updateResult.emit('Die Autorisierung war erfolgreich. Deine Ausweisdaten wurden hochgeladen.');
    } else if (result.reason === 'CanceledByUser') {
      this.cancelUpdateIdentDoc();
    }
  }
}
